import React, { useState, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

type HoverTooltipProps = {
    text: string | React.ReactNode;
    children: React.ReactNode;
    position?: TooltipPosition;
    disabled?: boolean;
    className?: string;
    useFixed?: boolean;
};

const positionClasses: Record<TooltipPosition, string> = {
    top: 'tw-bottom-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mb-1',
    bottom: 'tw-top-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mt-1',
    left: 'tw-right-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-me-1',
    right: 'tw-left-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-ms-1',
};

const HoverTooltip = ({
    text,
    children,
    position = 'top',
    disabled = false,
    className = '',
    useFixed = false,
}: HoverTooltipProps) => {
    const [visible, setVisible] = useState(false);
    const [fixedPos, setFixedPos] = useState<{ top: number; left: number } | null>(null);
    const [edgeOffset, setEdgeOffset] = useState(0);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const EDGE_MARGIN = 8;

    useLayoutEffect(() => {
        if (!visible || !tooltipRef.current) {
            return;
        }
        if (!useFixed && position !== 'top' && position !== 'bottom') {
            return;
        }
        const rect = tooltipRef.current.getBoundingClientRect();
        let overflow = 0;
        if (rect.left < EDGE_MARGIN) {
            overflow = rect.left - EDGE_MARGIN;
        } else if (rect.right > window.innerWidth - EDGE_MARGIN) {
            overflow = rect.right - (window.innerWidth - EDGE_MARGIN);
        }
        if (overflow !== 0) {
            setEdgeOffset((prev) => prev - overflow);
        }
    }, [visible, position, useFixed, fixedPos]);

    if (disabled) return <>{children}</>;

    const handleMouseEnter = () => {
        setEdgeOffset(0);
        if (useFixed && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            let top: number;
            let left: number;
            if (position === 'bottom') {
                top = rect.bottom + 4;
                left = rect.left + rect.width / 2;
            } else if (position === 'right') {
                top = rect.top + rect.height / 2;
                left = rect.right + 4;
            } else if (position === 'left') {
                top = rect.top + rect.height / 2;
                left = rect.left - 4;
            } else {
                top = rect.top - 4;
                left = rect.left + rect.width / 2;
            }
            setFixedPos({ top, left });
        }
        setVisible(true);
    };

    // § 194 popover chrome: `surface-raised` + `edge` + `shadow-ds-md`. The old
    // `tw-bg-white` / `tw-text-[#172b4d]` pair was light-only — a white card with
    // near-black text floating over the dark theme — and the bare `tw-border` had
    // no colour at all, so it fell back to preflight's default.
    const tooltipContent = (
        <div className="tw-px-2 tw-py-1 tw-bg-surface-raised tw-rounded-sm tw-shadow-ds-md tw-border tw-border-edge tw-flex tw-items-start tw-min-w-[220px] tw-max-w-[300px]">
            <span className="tw-min-w-0 tw-text-ink tw-text-xs tw-font-normal tw-leading-normal tw-break-words tw-text-left">
                {text}
            </span>
        </div>
    );

    return (
        <div
            ref={triggerRef}
            className={`tw-relative tw-inline-block ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => {
                setVisible(false);
                setEdgeOffset(0);
            }}
        >
            {children}
            {visible && !useFixed && (
                <div
                    ref={tooltipRef}
                    className={`tw-absolute tw-z-50 ${positionClasses[position]}`}
                    style={
                        edgeOffset !== 0 && (position === 'top' || position === 'bottom')
                            ? { transform: `translateX(calc(-50% + ${edgeOffset}px))` }
                            : undefined
                    }
                    role="tooltip"
                >
                    {tooltipContent}
                </div>
            )}
            {visible && useFixed && fixedPos && ReactDOM.createPortal(
                <div
                    ref={tooltipRef}
                    className="tw-fixed tw-z-[9999]"
                    style={
                        position === 'bottom'
                            ? { top: `${fixedPos.top}px`, left: `${fixedPos.left}px`, transform: `translateX(calc(-50% + ${edgeOffset}px))` }
                            : position === 'right'
                            ? { top: `${fixedPos.top}px`, left: `${fixedPos.left}px`, transform: 'translateY(-50%)' }
                            : position === 'left'
                            ? { top: `${fixedPos.top}px`, left: `${fixedPos.left}px`, transform: 'translate(-100%, -50%)' }
                            : { top: `${fixedPos.top}px`, left: `${fixedPos.left}px`, transform: `translate(calc(-50% + ${edgeOffset}px), -100%)` }
                    }
                    role="tooltip"
                >
                    {tooltipContent}
                </div>,
                document.body
            )}
        </div>
    );
};

export default HoverTooltip;
